import { payPeriodApi } from '@/lib/api/pay-periods.api'
import { createLogger } from '@/lib/logger'
import { queryKeys } from '../query-keys'
/**
 * Pay Periods Query Hooks
 * React Query hooks for pay period management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  AllUsersTimesheetRow,
  GeneratePayPeriodsRequest,
  MissingTimesheet,
  PayPeriod,
  PayPeriodSummary,
  PayPeriodTimesheetSummary,
  PayPeriodTypeEnum,
  TimeEntry,
  UserPayPeriod,
} from '@scrambl/shared'

const logger = createLogger('hooks/queries/usePayPeriodsQuery')

// Query keys
export const payPeriodKeys = {
  all: ['payPeriods'] as const,
  lists: () => [...payPeriodKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) =>
    [...payPeriodKeys.lists(), params] as const,
  current: () => [...payPeriodKeys.all, 'current'] as const,
  currentUser: () => [...payPeriodKeys.all, 'currentUser'] as const,
  details: () => [...payPeriodKeys.all, 'detail'] as const,
  detail: (id: string) => [...payPeriodKeys.details(), id] as const,
  summary: (id: string) => [...payPeriodKeys.all, 'summary', id] as const,
  missing: (id: string) => [...payPeriodKeys.all, 'missing', id] as const,
  userPayPeriods: (payPeriodId: string) =>
    [...payPeriodKeys.all, 'userPayPeriods', payPeriodId] as const,
  userPayPeriod: (payPeriodId: string, userId: string) =>
    [...payPeriodKeys.all, 'userPayPeriod', payPeriodId, userId] as const,
  timesheetPeriods: (params: Record<string, unknown>) =>
    [...payPeriodKeys.all, 'timesheetPeriods', params] as const,
  timesheetPeriodUsers: (periodId: string, params: Record<string, unknown>) =>
    [...payPeriodKeys.all, 'timesheetPeriodUsers', periodId, params] as const,
  myPeriods: (params: Record<string, unknown>) =>
    [...payPeriodKeys.all, 'myPeriods', params] as const,
  myPeriodStatus: (periodId: string) =>
    [...payPeriodKeys.all, 'myPeriodStatus', periodId] as const,
  myPeriodEntries: (periodId: string) =>
    [...payPeriodKeys.all, 'myPeriodEntries', periodId] as const,
  userPeriodEntries: (periodId: string, userId: string) =>
    [...payPeriodKeys.all, 'userPeriodEntries', periodId, userId] as const,
}
// =====================
// Query Hooks
// =====================

/**
 * Get all pay periods
 */
export function usePayPeriodsQuery(
  params: {
    status?: string
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...queryParams } = params

  return useQuery({
    queryKey: payPeriodKeys.list(queryParams),
    queryFn: () => payPeriodApi.getPayPeriods(queryParams),
    enabled,
    staleTime: 30000,
  })
}

/**
 * Get current pay period
 */
export function useCurrentPayPeriodQuery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: payPeriodKeys.current(),
    queryFn: () => payPeriodApi.getCurrentPeriod(),
    enabled: options.enabled ?? true,
    staleTime: 60000,
  })
}

/**
 * Get a specific pay period
 */
export function usePayPeriodQuery(
  id: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payPeriodKeys.detail(id),
    queryFn: () => payPeriodApi.getPayPeriod(id),
    enabled: (options.enabled ?? true) && !!id,
    staleTime: 30000,
  })
}

/**
 * Get pay period summary
 */
export function usePayPeriodSummaryQuery(
  id: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payPeriodKeys.summary(id),
    queryFn: () => payPeriodApi.getPeriodSummary(id),
    enabled: (options.enabled ?? true) && !!id,
    staleTime: 30000,
  })
}

/**
 * Get missing timesheets for a pay period
 */
export function useMissingTimesheetsQuery(
  id: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payPeriodKeys.missing(id),
    queryFn: () => payPeriodApi.getMissingTimesheets(id),
    enabled: (options.enabled ?? true) && !!id,
    staleTime: 30000,
  })
}

/**
 * Get all users' current timesheets (admin view)
 */
export function useAllUsersTimesheetsQuery(
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...payPeriodKeys.all, 'allUsersTimesheets'] as const,
    queryFn: () => payPeriodApi.getAllUsersCurrentTimesheets(),
    enabled: options.enabled ?? true,
    staleTime: 30000,
  })
}

/**
 * Get pay period summaries for timesheets view (lightweight, no user details)
 */
export function useTimesheetPeriodSummariesQuery(
  params: {
    status?: string
    periodType?: string
    startDate?: string
    endDate?: string
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...queryParams } = params
  return useQuery({
    queryKey: payPeriodKeys.timesheetPeriods(queryParams),
    queryFn: () => payPeriodApi.getTimesheetPeriodSummaries(queryParams),
    enabled,
    staleTime: 30000,
    placeholderData: previous => previous,
  })
}

/**
 * Get user rows for a specific pay period (on-demand when expanded)
 */
export function useTimesheetPeriodUsersQuery(
  periodId: string,
  params: {
    search?: string
    status?: string
    memberIds?: string[]
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...queryParams } = params
  return useQuery({
    queryKey: payPeriodKeys.timesheetPeriodUsers(periodId, queryParams),
    queryFn: () => payPeriodApi.getTimesheetPeriodUsers(periodId, queryParams),
    enabled: enabled && !!periodId,
    staleTime: 30000,
    placeholderData: previous => previous,
  })
}

// =====================
// Mutation Hooks
// =====================

/**
 * Create pay period mutation
 */
export function useCreatePayPeriodMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      name: string
      periodType: PayPeriodTypeEnum
      startDate: string
      endDate: string
    }) => payPeriodApi.createPayPeriod(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      toast.success('Pay period created successfully')
    },
    onError: error => {
      logger.error('Failed to create pay period:', error)
      toast.error('Failed to create pay period')
    },
  })
}

/**
 * Generate pay periods mutation
 */
export function useGeneratePayPeriodsMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: GeneratePayPeriodsRequest) =>
      payPeriodApi.generatePayPeriods(request),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      toast.success(
        `Generated ${result.created} pay periods (${result.skipped} already existed)`,
      )
    },
    onError: error => {
      logger.error('Failed to generate pay periods:', error)
      toast.error('Failed to generate pay periods')
    },
  })
}

/**
 * Close pay period mutation
 */
export function useClosePayPeriodMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, autoSubmit }: { id: string; autoSubmit?: boolean }) =>
      payPeriodApi.closePeriod(id, autoSubmit),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      queryClient.invalidateQueries({
        queryKey: payPeriodKeys.detail(variables.id),
      })
      toast.success('Pay period closed successfully')
    },
    onError: error => {
      logger.error('Failed to close pay period:', error)
      toast.error('Failed to close pay period')
    },
  })
}

/**
 * Reopen pay period mutation
 */
export function useReopenPayPeriodMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => payPeriodApi.reopenPeriod(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.detail(id) })
      toast.success('Pay period reopened successfully')
    },
    onError: error => {
      logger.error('Failed to reopen pay period:', error)
      toast.error('Failed to reopen pay period')
    },
  })
}

/**
 * Lock pay period mutation
 */
export function useLockPayPeriodMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => payPeriodApi.lockPeriod(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.detail(id) })
      toast.success('Pay period locked permanently')
    },
    onError: error => {
      logger.error('Failed to lock pay period:', error)
      toast.error('Failed to lock pay period')
    },
  })
}

// =====================
// User Pay Period Queries
// =====================

/**
 * Get current user's pay period (user-specific status)
 */
export function useCurrentUserPayPeriodQuery(
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payPeriodKeys.currentUser(),
    queryFn: () => payPeriodApi.getCurrentUserPeriod(),
    enabled: options.enabled ?? true,
    staleTime: 60000,
  })
}

/**
 * Get all UserPayPeriod records for the current user (with auto-backfill)
 */
export function useMyUserPayPeriodsQuery(
  params: {
    status?: string
    page?: number
    limit?: number
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...queryParams } = params
  return useQuery({
    queryKey: payPeriodKeys.myPeriods(queryParams),
    queryFn: () => payPeriodApi.getMyUserPayPeriods(queryParams),
    enabled,
    staleTime: 30000,
  })
}

/**
 * Get the current user's UPP status for a specific pay period
 */
export function useMyPeriodStatusQuery(
  periodId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payPeriodKeys.myPeriodStatus(periodId || ''),
    queryFn: () => payPeriodApi.getMyPeriodStatus(periodId!),
    enabled: (options.enabled ?? true) && !!periodId,
    staleTime: 30000,
  })
}

/**
 * Get time entries for the current user within a specific pay period.
 * The backend handles date filtering using DATE-to-DATE comparison (no timezone issues).
 */
export function useMyPeriodEntriesQuery(
  periodId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery<TimeEntry[]>({
    queryKey: payPeriodKeys.myPeriodEntries(periodId || ''),
    queryFn: () => payPeriodApi.getMyPeriodEntries(periodId!),
    enabled: (options.enabled ?? true) && !!periodId,
    staleTime: 30000,
  })
}

/**
 * Get time entries for a specific user within a specific effective pay period.
 */
export function useUserPeriodEntriesQuery(
  periodId: string | undefined,
  userId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery<TimeEntry[]>({
    queryKey: payPeriodKeys.userPeriodEntries(periodId || '', userId || ''),
    queryFn: () => payPeriodApi.getUserPeriodEntries(periodId!, userId!),
    enabled: (options.enabled ?? true) && !!periodId && !!userId,
    staleTime: 30000,
  })
}

/**
 * Refresh stored rate snapshots for a user's entries in a pay period.
 */
export function useRefreshUserPeriodRatesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      payPeriodId,
      userId,
    }: {
      payPeriodId: string
      userId: string
    }) => payPeriodApi.refreshUserPeriodRates(payPeriodId, userId),
    onSuccess: (result, { payPeriodId, userId }) => {
      queryClient.invalidateQueries({
        queryKey: payPeriodKeys.userPeriodEntries(payPeriodId, userId),
      })
      queryClient.invalidateQueries({
        queryKey: payPeriodKeys.userPayPeriod(payPeriodId, userId),
      })
      queryClient.invalidateQueries({
        queryKey: payPeriodKeys.summary(payPeriodId),
      })
      if (result.failed.length === 0) {
        toast.success(`Refreshed rates for ${result.updated} entries`)
      } else {
        toast.warning(
          `Refreshed ${result.updated} entries, ${result.failed.length} failed`,
        )
      }
    },
    onError: error => {
      logger.error('Failed to refresh user period rates:', error)
      toast.error('Failed to refresh rates')
    },
  })
}

/**
 * Get all user pay periods for a given pay period (admin view)
 */
export function useUserPayPeriodsQuery(
  payPeriodId: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payPeriodKeys.userPayPeriods(payPeriodId),
    queryFn: () => payPeriodApi.getUserPayPeriods(payPeriodId),
    enabled: (options.enabled ?? true) && !!payPeriodId,
    staleTime: 30000,
  })
}

/**
 * Get a specific user's pay period
 */
export function useUserPayPeriodQuery(
  payPeriodId: string,
  userId: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: payPeriodKeys.userPayPeriod(payPeriodId, userId),
    queryFn: () => payPeriodApi.getUserPayPeriod(payPeriodId, userId),
    enabled: (options.enabled ?? true) && !!payPeriodId && !!userId,
    staleTime: 30000,
  })
}

// =====================
// User Pay Period Mutations
// =====================

/**
 * Submit a user's pay period (admin action)
 */
export function useSubmitUserPeriodMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      payPeriodId,
      userId,
    }: {
      payPeriodId: string
      userId: string
    }) => payPeriodApi.submitUserPeriod(payPeriodId, userId),
    onSuccess: (_, { payPeriodId }) => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      queryClient.invalidateQueries({
        queryKey: payPeriodKeys.summary(payPeriodId),
      })
      toast.success('User timesheet submitted')
    },
    onError: error => {
      logger.error('Failed to submit user period:', error)
      toast.error('Failed to submit user timesheet')
    },
  })
}

/**
 * Approve a user's pay period (admin action)
 */
export function useApproveUserPeriodMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      payPeriodId,
      userId,
    }: {
      payPeriodId: string
      userId: string
    }) => payPeriodApi.approveUserPeriod(payPeriodId, userId),
    onSuccess: (_, { payPeriodId }) => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      queryClient.invalidateQueries({
        queryKey: payPeriodKeys.summary(payPeriodId),
      })
      toast.success('User timesheet approved')
    },
    onError: error => {
      logger.error('Failed to approve user period:', error)
      toast.error('Failed to approve user timesheet')
    },
  })
}

/**
 * Reopen a user's pay period (admin action)
 */
export function useReopenUserPeriodMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      payPeriodId,
      userId,
    }: {
      payPeriodId: string
      userId: string
    }) => payPeriodApi.reopenUserPeriod(payPeriodId, userId),
    onSuccess: (_, { payPeriodId }) => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      queryClient.invalidateQueries({
        queryKey: payPeriodKeys.summary(payPeriodId),
      })
      toast.success('User timesheet reopened')
    },
    onError: error => {
      logger.error('Failed to reopen user period:', error)
      toast.error('Failed to reopen user timesheet')
    },
  })
}

/**
 * Self-service recall: retract own submitted timesheet back to draft
 */
export function useRecallMySubmissionMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payPeriodId: string) =>
      payPeriodApi.recallMySubmission(payPeriodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
      // Recall reverts entries SUBMITTED → DRAFT, so the time-entry grid must
      // refetch too (it's keyed under queryKeys.timeEntries, not payPeriodKeys).
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
      toast.success('Submission recalled — your timesheet is back in draft')
    },
    onError: error => {
      logger.error('Failed to recall submission:', error)
      toast.error('Failed to recall submission')
    },
  })
}
